import React, {ReactElement, useEffect, useState} from "react";
import {INormalizedProduct} from "../../models/NormalizedProduct";
import {VendorsTable} from "./VendorsTable";
import {NotesDetails} from "./NotesDetails";
import {AttributesTable, IAttributeKV} from "./AttributesTable";
import BasicDetails from "./BasicDetails";
import AddToDestinationDialog from "../Common/AddToDestinationDialog";
import BasicDetailsTable from "./BasicDetailsTable";
import {
    DefaultButton, Dialog, DialogFooter, DialogType,
    IContextualMenuProps,
    Panel,
    PanelType,
    Pivot,
    PivotItem,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    Text
} from "@fluentui/react";
import LinkedItemsDetails from "./LinkedItemsDetails";
import {
    useDeleteProductImagesMutation,
    useGetItemDetailsQuery,
    usePutProductMutation,
    usePutTagsMutation,
    usePutUserTagsMutation
} from "../../store/Api";
import {cloneDeep} from "lodash";
import {ProductImages} from "./ProductImages";
import {ProductVideos} from "./ProductVideos";
import EditableImage from "./EditableImage";
import {IProductBase, IProductImage, IProductStorefrontEntry} from "../../models/Search";
import ProductTagsAutoSuggest from "../Search/ProductTagsAutoSuggest"
import SearchFilter from "../Common/SearchFilter";
import {ProductLinesTable} from "./ProductLinesTable";
import ProductStorefrontDetails from "./ProductStorefrontDetails";
import useCan, {PROCUREMENT, STOREFRONT_ADMIN, PRODUCT_PROMOS_ADMIN} from "../../permissions/ability";
import {toast} from "react-toastify";
import {addProductToDestination} from "../../logic/addToDestination";
import {IActiveDestination} from "../../models/Destination";
import {useDispatch} from "react-redux";
import {NormalizeResult} from "../../logic/Product";
import EditorVendorTable from "./EditableVendorsTable";
import DescriptionSourceSelection from "./DescriptionSourceSelection";
import StorefrontProductQuestionsForm from "../Storefront/StorefrontProductQuestionsForm";
import ProductPromoTab from "./ProductPromoTab";
import ProductPurchaseHistory from "./ProductPurchaseHistory";

interface IDetailsModalProps {
    product: string;
    catalog: string;
    open: boolean;
    onClose: () => void;
    showWorkspaceButton?: boolean;
    trigger?: ReactElement;
    fluid?: boolean;
    grid?: boolean;
    refreshingVendors?: boolean;
    preventEdit?: boolean;
    showConfigurationButton?: boolean;
    configure?: (configId: string) => void;
    onProcure?: (catalog: string, id: string, vendor?: string) => void;
    /** When set, "Add To..." becomes a split button whose main action adds straight to this destination
     *  (the workspace or bundle the omnibox search was opened from); the menu still reaches every other one. */
    addToDestination?: IActiveDestination;
    /** Tab to open on: 'media' for the images (a thumbnail click), 'vendor' for sourcing (a cost click). Default is Details. */
    initialTab?: 'details' | 'media' | 'vendor' | 'purchaseHistory';
}

const DetailsModal: React.FC<IDetailsModalProps> = (props) => {
    const [saving, setSaving] = useState(false);
    const [needsSaving, setNeedsSaving] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [deleteCustomImageFiles] = useDeleteProductImagesMutation();
    const [saveProduct] = usePutProductMutation();
    const [updateUserTags] = usePutUserTagsMutation();
    const [updateTags] = usePutTagsMutation();
    const [savingTags, setSavingTags] = useState<boolean>(false);
    const [updatedProduct, setUpdatedProduct] = useState<INormalizedProduct | undefined>()
    const [addToDialogOpen, setAddToDialogOpen] = useState<boolean>(false);
    const [addingToWorkspace, setAddingToWorkspace] = useState<boolean>(false);
    const addToDispatch = useDispatch();
    const destinationLabel = props.addToDestination?.name ?? (props.addToDestination?.type === 'bundle' ? 'the bundle' : 'the workspace');
    const addToContextDestination = async (target: INormalizedProduct) => {
        if (!props.addToDestination) return;
        setAddingToWorkspace(true);
        const toastId = toast.loading(`Adding ${target.name ?? 'product'} to ${destinationLabel}…`);
        try {
            const ok = await addProductToDestination(addToDispatch, props.addToDestination, target);
            if (!ok) throw new Error('add failed');
            toast.update(toastId, {type: 'success', render: `${target.name ?? 'Product'} added to ${destinationLabel}`, isLoading: false, autoClose: 3000});
            props.onClose();
        } catch {
            toast.update(toastId, {type: 'error', render: `Failed to add ${target.name ?? 'product'} to ${destinationLabel}`, isLoading: false, autoClose: 4000});
        } finally {
            setAddingToWorkspace(false);
        }
    };
    const [attrArray, setAttrArray] = useState<IAttributeKV[]>([]);
    const [productImages, setProductImages] = useState<IProductImage[]>([])
    const [productVideoUrls, setProductVideoUrls] = useState<string[]>([])
    const [facetArray, setFacetArray] = useState<IAttributeKV[]>([]);
    const [filterArray, setFilterArray] = useState<IAttributeKV[]>([]);
    const [tags, setTags] = useState<string[]>()
    const [userTags, setUserTags] = useState<string[]>()
    const [productLines, setProductLines] = useState<string[]>([])
    const [updatedProductImageUrl, setProductImageUrl] = useState<string>();
    const [productImagesToDelete, setProductImagesToDelete] = useState<IProductImage[]>([]);
    const [storefrontOverrideMarkup, setStorefrontOverrideMarkup] = useState<number | undefined>();
    const [storefrontOverrideMarkupType, setStorefrontOverrideMarkupType] = useState<string | undefined>()
    const [storefrontAlwaysAvailable, setStorefrontAlwaysAvailable] = useState<boolean>();
    const [showLinkedItemsOnStorefront, setShowLinkedItemsOnStorefront] = useState<boolean>();
    const [hidePriceOnStorefront, setHidePriceOnStorefront] = useState<boolean>();
    const [includeInAllStorefronts, setIncludeInAllStorefronts] = useState<boolean>();
    const [featureInAllStorefronts, setFeatureInAllStorefronts] = useState<boolean>();
    const [storefronts, setStorefronts] = useState<{[key:string]: IProductStorefrontEntry} | undefined>();
    const [includedStorefronts, setIncludedStorefronts] = useState<string[]>();
    const [featuredStorefronts, setFeaturedStorefronts] = useState<string[]>();
    const canStorefrontAdmin = useCan(STOREFRONT_ADMIN);
    const canProcurement = useCan(PROCUREMENT);
    const details = useGetItemDetailsQuery({id: props.product, catalog: props.catalog}, {skip: !props.product || props.product == '' || !props.open});
    const [product, setProduct] = useState<INormalizedProduct | undefined>(undefined);
    // UX gate only; the API re-checks the permission and the org entitlement on every write.
    const canManagePromos = useCan(PRODUCT_PROMOS_ADMIN);
    const [storefrontOrderPriority, setStorefrontOrderPriority] = useState<number | undefined>(undefined);
    const [globalStorefrontMinimumPrice, setGlobalStorefrontMinimumPrice] = useState<number | undefined>(undefined);
    const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

    useEffect(() => {
        if(details.data) {
            const prod = NormalizeResult(details.data)
            setProduct(prod);
        }
    }, [details.data]);

    useEffect(() => {
        return () => {
            setProduct(undefined)
            setStorefrontOrderPriority(undefined)
            setGlobalStorefrontMinimumPrice(undefined)
            setFeaturedStorefronts(undefined)
            setIncludedStorefronts(undefined)
            setStorefronts(undefined)
            setFeatureInAllStorefronts(undefined)
            setIncludeInAllStorefronts(undefined)
            setStorefrontAlwaysAvailable(undefined)
            setShowLinkedItemsOnStorefront(undefined)
            setHidePriceOnStorefront(undefined)
            setStorefrontOverrideMarkupType(undefined)
            setStorefrontOverrideMarkup(undefined)
            setProductImagesToDelete([])
            setProductImageUrl(undefined)
            setProductLines([])
            setUserTags([])
            setTags([])
            setFilterArray([])
            setFacetArray([])
            setProductImages([])
            setProductVideoUrls([])
            setAttrArray([])
            setUpdatedProduct(undefined)
            setSavingTags(false)
            setEditMode(false)
            setNeedsSaving(false)
            setSaving(false)
            setShowCloseConfirmation(false)
        };
    }, []);

    useEffect(() => {
        const attributes = product?.attributes != null
            ? Object.entries(product?.attributes).map((a) => ({key: a[0], value: a[1]}))
            : [];
        const facets = product?.facets != null
            ? Object.entries(product?.facets).map((a) => ({key: a[0], value: a[1]}))
            : [];
        const filters = product?.filters
            ? Object.entries(product?.filters).map((a) => ({key: a[0], value: a[1]}))
            : [];
        const images = product?.images ?? [];
        const videoUrls = product?.videoUrls ?? [];
        const productLines = product?.productLines ?? [];
        setProductImageUrl(product?.image);
        setAttrArray(attributes);
        setFacetArray(facets);
        setFilterArray(filters);
        setProductImages(images);
        setProductVideoUrls(videoUrls);
        setTags(product?.tags);
        setUserTags(product?.userTags);
        setProductLines(productLines);
    }, [
        product?.attributes,
        product?.facets,
        product?.filters,
        product?.images,
        product?.videoUrls,
        product?.image,
        product?.tags,
        product?.userTags,
        product?.productLines
    ]);

    useEffect(() => {
        setStorefrontOverrideMarkup(product?.storefrontOverrideMarkup);
        setStorefrontOverrideMarkupType(product?.storefrontOverrideMarkupType);
        setStorefronts(product?.storefronts);
        setIncludedStorefronts(product?.includedStorefronts);
        setFeaturedStorefronts(product?.featuredStorefronts);
        setStorefrontAlwaysAvailable(product?.storefrontAlwaysAvailable)
        setShowLinkedItemsOnStorefront(product?.showLinkedItemsOnStorefront ?? undefined)
        setHidePriceOnStorefront(product?.hidePriceOnStorefront)
        setIncludeInAllStorefronts(product?.includeInAllStorefronts);
        setFeatureInAllStorefronts(product?.featureInAllStorefronts);
        setStorefrontOrderPriority(product?.storefrontOrderPriority);
        setGlobalStorefrontMinimumPrice(product?.globalStorefrontMinimumPrice);
    }, [
        product?.storefrontOverrideMarkup,
        product?.storefrontOverrideMarkupType,
        product?.storefronts,
        product?.storefrontAlwaysAvailable,
        product?.showLinkedItemsOnStorefront,
        product?.hidePriceOnStorefront,
        product?.includeInAllStorefronts,
        product?.includedStorefronts,
        product?.featuredStorefronts,
        product?.featureInAllStorefronts,
        product?.storefrontOrderPriority,
        product?.globalStorefrontMinimumPrice
    ]);

    const enableEditMode = () => {
        if (!props.preventEdit) {
            setUpdatedProduct(product);
            setEditMode(true)
        }
    }

    const initialState = cloneDeep(product)
    const addProductImageToDeleteQueue = (image: IProductImage) => {
        const currentImagesToDelete = cloneDeep(productImagesToDelete)
        currentImagesToDelete.push(image)
        setProductImagesToDelete(currentImagesToDelete);
    }

    const mapUpdatedDetails = () => {
        if (updatedProduct) {
            const prod = cloneDeep(updatedProduct)
            prod.storefrontOverrideMarkup = storefrontOverrideMarkup;
            prod.storefrontOverrideMarkupType = storefrontOverrideMarkupType;
            prod.storefrontAlwaysAvailable = storefrontAlwaysAvailable;
            prod.showLinkedItemsOnStorefront = showLinkedItemsOnStorefront;
            prod.hidePriceOnStorefront = hidePriceOnStorefront;
            prod.storefronts = storefronts;
            prod.includedStorefronts = includedStorefronts;
            prod.featuredStorefronts = featuredStorefronts;
            prod.includeInAllStorefronts = includeInAllStorefronts;
            prod.featureInAllStorefronts = featureInAllStorefronts;
            prod.storefrontOrderPriority = storefrontOrderPriority;
            prod.globalStorefrontMinimumPrice = globalStorefrontMinimumPrice;
            if (updatedProductImageUrl) {
                prod.image = updatedProductImageUrl
            }
            if (attrArray) {
                prod.attributes = Object.assign({}, ...attrArray.map((x) => ({[x.key]: x.value})));
            }
            if (productImages) {
                prod.images = productImages
            }
            prod.videoUrls = productVideoUrls.map(v => v.trim()).filter(v => v !== '');
            if (facetArray) {
                prod.facets = Object.assign({}, ...facetArray.map((x) => ({[x.key]: x.value})));
            }
            if (filterArray) {
                prod.filters = Object.assign({}, ...filterArray.map((x) => ({[x.key]: x.value})));
            }
            if (productLines) {
                prod.productLines = productLines;
            }
            return prod;
        }
        return undefined;
    }
    const save = (close?: boolean) => {
        const newProduct = mapUpdatedDetails();
        if (newProduct) {
            setSaving(true);
            if (productImagesToDelete && productImagesToDelete.length > 0) {
                deleteCustomImageFiles({imageIds: productImagesToDelete.map(a => a.id), productId: newProduct?.id}).
                then(() => {
                    saveProduct(newProduct).then(() => {
                        setSaving(false);
                        if(close) {
                            setUpdatedProduct(undefined);
                            props.onClose();
                            setEditMode(false);
                        }
                        else setUpdatedProduct(newProduct);
                });});
            }
            else {
                saveProduct(newProduct).then(() => {
                    setSaving(false);
                    if(close) {
                        setUpdatedProduct(undefined);
                        props.onClose();
                        setEditMode(false);
                    }
                    else setUpdatedProduct(newProduct);
                });
            }
        }
        setNeedsSaving(false)
    }

    const includeInAllStorefrontsForcedSave = (close?: boolean, includeInAll?: boolean) => {
        const newProduct = mapUpdatedDetails();
        if (newProduct) {
            setSaving(true);
            newProduct.includeInAllStorefronts = includeInAll;
            if (productImagesToDelete && productImagesToDelete.length > 0) {
                deleteCustomImageFiles({imageIds: productImagesToDelete.map(a => a.id), productId: newProduct?.id}).
                then(() => {
                    saveProduct(newProduct).then(() => {
                        setSaving(false);
                        if(close) {
                            setUpdatedProduct(undefined);
                            props.onClose();
                            setEditMode(false);
                        }
                        else setUpdatedProduct(newProduct);
                    });});
            }
            else {
                saveProduct(newProduct).then(() => {
                    setSaving(false);
                    if(close) {
                        setUpdatedProduct(undefined);
                        props.onClose();
                        setEditMode(false);
                    }
                    else setUpdatedProduct(newProduct);
                });
            }
        }
    }
    
    const onIncludeInAllStorefrontsChange = (include?: boolean) => {
        setIncludeInAllStorefronts(include);
        includeInAllStorefrontsForcedSave(false, include)
    }

    const updateProductImageUrl = (id: string, url: string) => {
        setProductImageUrl(url)
    }
    const productName = product?.name ?? 'Product Details';
    const mfp = product?.manufacturerPartNumber ? `${product?.manufacturerPartNumber}` : '';
    const productImage: IProductImage = {
        name: "Product Image",
        url: updatedProductImageUrl ?? product?.image ?? '',
        type: "Product",
        id: product?.id ?? '',
        isCustom: true
    };

    const removeUserTag = (tag: string) => {
        const tagCopy = cloneDeep(userTags) ?? [];
        const index = tagCopy.findIndex(a => a == tag);
        if (index !== -1 && product) {
            tagCopy.splice(index, 1)
            setUserTags(tagCopy);
            setSavingTags(true);
            updateUserTags({productId: product?.id, catalog: product?.catalog, tags: tagCopy})
                .unwrap()
                .then(() => toast.success("Removed User Tag"))
                .catch(() => toast.error("Failed to remove tag"))
                .finally(() => setSavingTags(false));
        }
    }
    const removeTag = (tag: string) => {
        const tagCopy = cloneDeep(tags) ?? [];
        const index = tagCopy.findIndex(a => a == tag);
        if (index !== -1 && product) {
            tagCopy.splice(index, 1)
            setTags(tagCopy);
            setSavingTags(true);
            updateTags({productId: product?.id, catalog: product?.catalog, tags: tagCopy})
                .unwrap()
                .then(() => toast.success("Removed Global Tag"))
                .catch(() => toast.error("Failed to remove tag"))
                .finally(() => setSavingTags(false));
        }
    }
    const addUserTag = (tag: string) => {
        const userTagCopy = cloneDeep(userTags) ?? [];
        tag = tag.replace("||", "")
        if (userTagCopy.indexOf(tag) === -1 && product) {
            userTagCopy.push(tag);
            setUserTags(userTagCopy);
            setSavingTags(true);
            updateUserTags({productId: product.id, catalog: product.catalog, tags: userTagCopy})
                .unwrap()
                .then(() => toast.success("Added User Tag"))
                .catch(() => toast.error("Failed to add tag"))
                .finally(() => setSavingTags(false));
        }
    }
    const addTag = (tag: string) => {
        const tagCopy = cloneDeep(tags) ?? [];
        tag = tag.replace("||", "")
        if (tagCopy.indexOf(tag) === -1 && product) {
            tagCopy.push(tag);
            setTags(tagCopy);
            setSavingTags(true);
            updateTags({productId: product.id, catalog: product.catalog, tags: tagCopy})
                .unwrap()
                .then(() => toast.success("Added Global Tag"))
                .catch(() => toast.error("Failed to add tag"))
                .finally(() => setSavingTags(false));
        }
    }

    const setProductFromBase = (base: IProductBase) => {
        if(base && product) {
            setNeedsSaving(true)
            setUpdatedProduct({
                ...product,
                ...base,
                id: product.id,
                catalog: product.catalog,
            });
        }
    }

    const menuProps: IContextualMenuProps = {
        items: [
            {
                key: 'saveAndClose',
                text: 'Save and Close',
                disabled: !needsSaving,
                iconProps: { iconName: 'SaveAndClose' },
                onClick: () => {
                    save(true);
                }
            }
        ],
    };

    const dialogContentProps = {
        type: DialogType.normal,
        title: 'Close Confirmation',
        subText: `Are you sure you want to close this panel? Unsaved changes will be discarded`,
        
    };
    const dialogModalProps = {
        isBlocking: true,
        styles: { main: { maxWidth: 450 } },
    };

    return (
        <div>
            {/* Sibling of the Panel, matching the close-confirmation dialog below — dialogs raised
                from inside this screen live outside the Panel's subtree. */}
            {addToDialogOpen && product &&
                <AddToDestinationDialog
                    isOpen={addToDialogOpen}
                    onDismiss={() => setAddToDialogOpen(false)}
                    products={[product]}
                    catalog={product.catalog}
                    onAdded={() => props.onClose()}
                />
            }
            {showCloseConfirmation && (
                <Dialog
                    hidden={false}
                    onDismiss={() => setShowCloseConfirmation(false)}
                    dialogContentProps={dialogContentProps}
                    modalProps={dialogModalProps}
                >
                    <DialogFooter>
                        <PrimaryButton onClick={() => props.onClose() } text="Yes" />
                        <DefaultButton onClick={() => setShowCloseConfirmation(false)} text="No" />
                    </DialogFooter>
                </Dialog>
            )}
            <Panel
                className={"detail-panel"}
                onOuterClick={() => {console.log("swallowing modal close event")}}
                isOpen={props.open}
                onDismiss={() => {
                    props.onClose()
                }}
                type={PanelType.large}
                isFooterAtBottom={true}
                styles={{
                    commands: {
                        background: "white"
                    },
                    footer: {
                        background: "white"
                    },
                    navigation: {
                        flex: '1 1 auto',
                    },
                }}
                onRenderHeader={() => {
                    return (
                        <div style={{flexGrow: 1, paddingLeft: '25px', background: "white"}}>
                            {product ? 
                            <Stack horizontal disableShrink>
                                <Stack.Item align='auto'>
                                    <EditableImage 
                                        image={productImage} 
                                        editMode={editMode} 
                                        onChange={(a, b) => {
                                            setNeedsSaving(true)
                                            setProductImageUrl(b)
                                        }}/>
                                </Stack.Item>
                                <Stack.Item align='center' style={{paddingLeft: '1em'}}>
                                    <span data-testid='product-details-name'>
                                        <Text variant={'xLarge'} block>
                                            {productName}
                                        </Text>
                                    </span>
                                    <Text variant={'small'} block>
                                        Manufacturer Part Number: {mfp}
                                    </Text>
                                </Stack.Item>
                            </Stack> : 
                            <Spinner label="Loading..." ariaLive="assertive" style={{margin: '5em'}}/>
                            }
                        </div>
                    )
                }}
                onRenderFooterContent={() => (
                    <div style={{background: "white"}}>
                        <Stack horizontal tokens={{childrenGap: '1em'}}>
                        {editMode
                            ? <><DefaultButton
                                split
                                splitButtonAriaLabel="More save actions"
                                menuProps={menuProps}
                                text={saving ? "Saving" : "Save"}
                                iconProps={{iconName: 'Save'}}
                                disabled={saving || !needsSaving}
                                primary
                                
                                onClick={() => {
                                    save();
                                }}
                            />
                                <DefaultButton
                                    text={"Cancel"}
                                    iconProps={{iconName: 'cancel'}}
                                    disabled={saving}
                                    onClick={() => {
                                        if(needsSaving) {
                                            setShowCloseConfirmation(true)
                                        }
                                        else {
                                            props.onClose()
                                        }
                                    }}
                                    style={{marginLeft: '1em'}}
                                />
                            </>
                            : <DefaultButton
                                text="Edit"
                                iconProps={{iconName: 'Edit'}}
                                primary
                                disabled={props.preventEdit ?? false}
                                onClick={() => {
                                    if (product) {
                                        enableEditMode();
                                    }
                                }}
                            />
                        }
                        {product && props.showConfigurationButton && product.isConfigurable && props.configure &&
                            <DefaultButton
                                text="Configure"
                                primary
                                onClick={() => {product.configuratorProduct && props.configure?.(product.configuratorProduct);}}
                                />
                        }
                        {product && !editMode && props.addToDestination &&
                            <PrimaryButton
                                split
                                text={`Add to ${destinationLabel}`}
                                iconProps={{iconName: 'Add'}}
                                disabled={addingToWorkspace}
                                onClick={() => void addToContextDestination(product)}
                                splitButtonAriaLabel='More add destinations'
                                menuProps={{
                                    items: [{
                                        key: 'other',
                                        text: 'Add to another destination…',
                                        iconProps: {iconName: 'Add'},
                                        onClick: () => setAddToDialogOpen(true)
                                    }]
                                }}
                            />}
                        {product && !editMode && !props.addToDestination
                            ? <PrimaryButton
                                text="Add To..."
                                iconProps={{iconName: 'Add'}}
                                onClick={() => setAddToDialogOpen(true)}
                            />
                            : null
                        }
                        {canProcurement && props.onProcure && product
                            ? <DefaultButton
                                text="Procure"
                                primary
                                onClick={() => {props.onProcure?.(product.catalog, product.id, undefined);}}
                                />
                            : null
                        }
                        </Stack>
                    </div>
                )}
            >
                {product
                    ? (
                        <>
                            <div>
                                <Pivot overflowBehavior='menu' styles={{itemContainer: {marginTop: '1em'}}} defaultSelectedKey={props.initialTab ?? 'details'}>
                                    <PivotItem headerText='Details' itemKey='details'>
                                        <BasicDetails
                                            editMode={editMode}
                                            product={editMode && updatedProduct ? updatedProduct : product}
                                            id={product?.id}
                                            onChange={setProductFromBase}
                                        />
                                    </PivotItem>
                                    <PivotItem headerText='Attributes'>
                                        <>
                                            <BasicDetailsTable
                                                product={editMode && updatedProduct ? updatedProduct : product}
                                                editMode={editMode}
                                                onChange={setProductFromBase}
                                                catalog={product?.catalog}
                                            />
                                            <AttributesTable
                                                title='Attributes'
                                                attributes={attrArray}
                                                onUpdate={(a) => {
                                                    setNeedsSaving(true);
                                                    setAttrArray(a)}}
                                                editMode={editMode}
                                            />
                                            <AttributesTable
                                                title='Facets'
                                                attributes={facetArray}
                                                onUpdate={(a) => {
                                                    setNeedsSaving(true);
                                                    setFacetArray(a)}}
                                                editMode={editMode}
                                            />
                                            <AttributesTable
                                                title='Filters'
                                                attributes={filterArray}
                                                onUpdate={(a) => {
                                                    setNeedsSaving(true);
                                                    setFilterArray(a)}}
                                                editMode={editMode}
                                            />
                                            <ProductLinesTable
                                                title='Product Lines'
                                                productLines={productLines}
                                                editMode={editMode}
                                                onUpdate={(a) => {
                                                    setNeedsSaving(true);
                                                    setProductLines(a)}}>
                                            </ProductLinesTable>
                                        </>
                                    </PivotItem>
                                    {/* <PivotItem headerText='Extensions'>
                                </PivotItem> */}
                                    <PivotItem headerText='Tags'>
                                        <h4>Global Tags</h4>
                                        <ProductTagsAutoSuggest
                                            onCreate={(tag) => addTag(tag)}
                                            editable={true}
                                            testId="global-tag-input"/>
                                        {tags && tags.length > 0 ?
                                            tags?.map((tag) =>
                                                <SearchFilter key={tag} color={"#9BD3AB"} protected={false}
                                                              onDelete={() => removeTag(tag)}>{tag}</SearchFilter>
                                            ) : <div>No Global Tags</div>}
                                        <h4>My Tags</h4>
                                        <ProductTagsAutoSuggest
                                            onCreate={(tag) => addUserTag(tag)}
                                            editable={true}
                                            testId="user-tag-input"/>
                                        {userTags && userTags.length > 0 ?
                                            userTags?.map((tag) =>
                                                <SearchFilter key={tag} color={"#F9D585"} protected={false}
                                                              onDelete={() => removeUserTag(tag)}>
                                                    {tag}</SearchFilter>
                                            ) : <div>No User Tags</div>}
                                        {savingTags && <Spinner size={SpinnerSize.large}></Spinner>}
                                    </PivotItem>
                                    <PivotItem headerText='Vendor' itemKey='vendor'>
                                        <DescriptionSourceSelection
                                            product={updatedProduct ?? product}
                                            onSelectionChanged={(source) => {
                                                if (updatedProduct) {
                                                    setNeedsSaving(true);
                                                    setUpdatedProduct({...updatedProduct, descriptionSource: source})
                                                }
                                            }}
                                            editMode={editMode}
                                        />
                                        <hr style={{marginTop: '1em', marginBottom: '1em'}}/>
                                        {editMode ? 
                                            <EditorVendorTable product={updatedProduct || product} onChange={setProductFromBase}/>
                                             : <VendorsTable
                                                vendors={product?.vendors ?? []}
                                                product={product}
                                                refreshing={props.refreshingVendors}
                                            />
                                            }
                                    </PivotItem>
                                    {/* <PivotItem headerText='Insights'>
                                </PivotItem> */}
                                    <PivotItem headerText='Linked Products'>
                                        <LinkedItemsDetails product={product}/>
                                    </PivotItem>
                                    {canManagePromos && (
                                        <PivotItem headerText='Promos'>
                                            <ProductPromoTab
                                                editMode={editMode}
                                                promos={(editMode ? updatedProduct : product)?.promos}
                                                onChange={(promos) => {
                                                    setUpdatedProduct({...(updatedProduct ?? product)!, promos});
                                                    setNeedsSaving(true);
                                                }}
                                            />
                                        </PivotItem>
                                    )}
                                    <PivotItem headerText='Media' itemKey='media'>
                                        <ProductImages
                                            thumbnail={productImage.url}
                                            onThumbnailChange={(v) => {setNeedsSaving(true);
                                                setProductImageUrl(v)
                                            }}
                                            images={productImages}
                                            editMode={editMode}
                                            title={"Product Images"}
                                            onUpdate={(v) => {
                                                setNeedsSaving(true);
                                                setProductImages(v)
                                            }}
                                            onSetExternalProduct={(ext) => {
                                                setNeedsSaving(true);
                                                updateProductImageUrl(ext.id, ext.url)}}
                                            productId={product?.id}
                                            onDelete={addProductImageToDeleteQueue}
                                        />
                                        <ProductVideos
                                            title={"Product Videos"}
                                            videoUrls={productVideoUrls}
                                            editMode={editMode}
                                            onUpdate={(v) => {
                                                setNeedsSaving(true);
                                                setProductVideoUrls(v);
                                            }}
                                        />
                                    </PivotItem>
                                    <PivotItem headerText='Notes'>
                                        <NotesDetails disabled={!editMode} product={{catalog: product?.catalog, id: product?.id}}/>
                                    </PivotItem>
                                    <PivotItem headerText='Purchase History' itemKey='purchaseHistory'>
                                        <ProductPurchaseHistory catalog={product.catalog} productId={product.id}/>
                                    </PivotItem>
                                    {canStorefrontAdmin && <PivotItem headerText='Storefront'>
                                        <ProductStorefrontDetails
                                            onChangeAnything={() => setNeedsSaving(true)}
                                            productId={product.id}
                                            includeInAllStorefronts={includeInAllStorefronts}
                                            featureInAllStorefronts={featureInAllStorefronts}
                                            storefrontAlwaysAvailable={storefrontAlwaysAvailable}
                                            showLinkedItemsOnStorefront={showLinkedItemsOnStorefront}
                                            hidePriceOnStorefront={hidePriceOnStorefront}
                                            editMode={editMode}
                                            storefrontOverrideMarkup={storefrontOverrideMarkup}
                                            storefrontOverrideMarkupType={storefrontOverrideMarkupType}
                                            storefronts={storefronts}
                                            onStorefrontsChange={setStorefronts}
                                            includedStorefronts={includedStorefronts}
                                            onIncludedStorefrontsChange={setIncludedStorefronts}
                                            featuredStorefronts={featuredStorefronts}
                                            onFeaturedStorefrontsChange={setFeaturedStorefronts}
                                            onStorefrontOverrideMarkupChange={setStorefrontOverrideMarkup}
                                            onStorefrontOverrideMarkupTypeChange={setStorefrontOverrideMarkupType}
                                            onStorefrontAlwaysAvailableChange={setStorefrontAlwaysAvailable}
                                            onShowLinkedItemsOnStorefrontChange={setShowLinkedItemsOnStorefront}
                                            onHidePriceOnStorefrontChange={setHidePriceOnStorefront}
                                            onStorefrontIncludeInAllStorefrontsChange={onIncludeInAllStorefrontsChange}
                                            onStorefrontFeatureInAllStorefrontsChange={setFeatureInAllStorefronts}
                                            storefrontOrderPriority={storefrontOrderPriority}
                                            onStorefrontOrderPriorityChange={setStorefrontOrderPriority}
                                            globalStorefrontMinimumPrice={globalStorefrontMinimumPrice}
                                            onGlobalStorefrontMinimumPriceChange={setGlobalStorefrontMinimumPrice}
                                            />
                                    </PivotItem>}
                                    <PivotItem headerText='Storefront Questions'>
                                        <StorefrontProductQuestionsForm canEdit={editMode} productId={product.id} catalog={product.catalog}/>
                                    </PivotItem>
                                </Pivot>
                            </div>
                        </>
                    )
                    : null
                }
            </Panel>
        </div>
    )
}

export default DetailsModal;